import { router } from 'expo-router';
import { useAtomValue, useSetAtom } from 'jotai';
import * as React from 'react';
import { useState } from 'react';
import {
    BodyLargeEmphasized,
    BodyMediumRegular,
    BodySmallRegular,
    Button,
    ButtonSize,
    ButtonVariant,
    FruitfulCustomIcon,
    LabelEmphasized,
    Shadows,
    Sheet,
    Spinner,
    Switch,
    View,
    XStack,
    YStack,
} from 'smoothie';

import {
    centsToFormattedDollars,
    getDefaultedAccountNickname,
} from '../../../../common/utils/bankingUtils';
import useGetPaycheckConfigs from '../../../hooks/paycheck-routing/useGetPaycheckConfigs';
import useUpdatePaycheckConfig from '../../../hooks/paycheck-routing/useUpdatePaycheckConfig';
import useGetAllBankAccountDetails from '../../../hooks/useGetAllBankAccountDetails';
import {
    currentlyEditingConfigAtom,
    existingPaycheckRoutingConfigsAtom,
    selectedIncomeAtom,
    selectedIncomeBankAccountAtom,
} from '../../../state/PaycheckRoutingAtoms';
import type { BankAccountDisplayDetail } from '../../../types/BankAccount';
import {
    type PaycheckRoutingIncomeDetail,
    PaycheckRoutingRulesMode,
    PaycheckRoutingStatus,
} from '../../../types/PaycheckRouting';
import {
    findMatchingRoutingConfigForPaycheck,
    getClosedAccountsInConfig,
} from '../helpers';

interface DetectedIncomeCardProps {
    income: PaycheckRoutingIncomeDetail;
    bankAccountDetails: BankAccountDisplayDetail;
    showSetupButton?: boolean;
    isOverview?: boolean;
}

const DetectedIncomeCard = ({
    income,
    bankAccountDetails,
    showSetupButton,
    isOverview,
}: DetectedIncomeCardProps) => {
    const [
        isConfirmDeactivatePaycheckConfigSheetOpen,
        setIsConfirmDeactivatePaycheckConfigSheetOpen,
    ] = useState(false);
    const [isLoadingActivationStatus, setIsLoadingActivationStatus] =
        useState(false);

    const { mutateAsync: updateStatus } = useUpdatePaycheckConfig();
    const { refetch: refetchConfigs } = useGetPaycheckConfigs();

    const setSelectedIncome = useSetAtom(selectedIncomeAtom);
    const setSelectedIncomeBankAccount = useSetAtom(
        selectedIncomeBankAccountAtom,
    );
    const setCurrentlyEditingConfig = useSetAtom(currentlyEditingConfigAtom);

    const existingPaycheckRoutingConfigs = useAtomValue(
        existingPaycheckRoutingConfigsAtom,
    );

    const { closedAccountDetails } = useGetAllBankAccountDetails(false);

    const config = findMatchingRoutingConfigForPaycheck(
        income,
        existingPaycheckRoutingConfigs,
    );

    const { amount, originator } = income ?? {};
    const { icon, color } = bankAccountDetails ?? {};

    const needsAttention =
        getClosedAccountsInConfig({ config, closedAccountDetails }).length > 0;

    const updateConfig = async (checked: boolean) => {
        if (config?.id != null) {
            setIsLoadingActivationStatus(true);
            await updateStatus(
                {
                    body: {
                        status: checked
                            ? PaycheckRoutingStatus.ACTIVE
                            : PaycheckRoutingStatus.INACTIVE,
                    },
                    urlParams: {
                        configId: config.id,
                    },
                },
                {
                    onSuccess: async () => {
                        await refetchConfigs();
                        // there's a slider animation that we cannot get around here. adding this to hide the animation before the spinner goes off. sorry. i'm so sorry.
                        setTimeout(() => {
                            setIsLoadingActivationStatus(false);
                        }, 500);
                    },
                },
            );
        }
    };

    return (
        <YStack
            backgroundColor="$white"
            paddingHorizontal={20}
            paddingVertical={12}
            borderWidth={1}
            borderColor="$gray200"
            borderRadius={20}
            style={{ ...Shadows.SMALL }}
            gap={16}
        >
            <View
                backgroundColor="$gray100"
                marginHorizontal={-20}
                marginVertical={-12}
                marginBottom={0}
                paddingHorizontal={20}
                paddingVertical={12}
                borderTopLeftRadius={20}
                borderTopRightRadius={20}
            >
                <XStack justifyContent="space-between" alignItems="center">
                    <XStack alignItems="center" gap={8}>
                        <FruitfulCustomIcon
                            customSize={{ iconSize: 16, padding: 10 }}
                            icon={icon}
                            color={color}
                        />
                        <BodyMediumRegular color="$gray1200">
                            {getDefaultedAccountNickname(bankAccountDetails)}
                        </BodyMediumRegular>
                    </XStack>
                    {showSetupButton &&
                        config?.id != null &&
                        !isLoadingActivationStatus && (
                            <Switch
                                isChecked={
                                    config?.status ===
                                    PaycheckRoutingStatus.ACTIVE
                                }
                                onCheckedChange={async () => {
                                    if (
                                        config?.status ===
                                        PaycheckRoutingStatus.ACTIVE
                                    ) {
                                        setIsLoadingActivationStatus(true);
                                        setIsConfirmDeactivatePaycheckConfigSheetOpen(
                                            true,
                                        );
                                    } else {
                                        await updateConfig(true);
                                    }
                                }}
                            />
                        )}
                    {showSetupButton &&
                        config?.id != null &&
                        isLoadingActivationStatus && <Spinner />}
                </XStack>
            </View>
            <YStack gap={20}>
                <XStack alignItems="center" justifyContent="space-between">
                    <YStack gap={4}>
                        {isOverview && needsAttention && (
                            <View
                                backgroundColor="$orange400"
                                borderRadius={20}
                                marginBottom={8}
                            >
                                <LabelEmphasized
                                    color="$orange1000"
                                    paddingHorizontal={8}
                                    paddingVertical={4}
                                >
                                    NEEDS ATTENTION
                                </LabelEmphasized>
                            </View>
                        )}
                        <BodyMediumRegular>{originator}</BodyMediumRegular>
                        <BodySmallRegular color="$gray1000">
                            Direct deposit
                        </BodySmallRegular>
                    </YStack>
                    <BodyLargeEmphasized
                        color="$green1100"
                        textAlign="right"
                    >{`+${centsToFormattedDollars(amount)}`}</BodyLargeEmphasized>
                </XStack>
                {showSetupButton && config?.status == null && (
                    <Button
                        variant={ButtonVariant.PRIMARY}
                        size={ButtonSize.SMALL}
                        text="Split this paycheck"
                        onPress={() => {
                            setSelectedIncome(income);
                            setSelectedIncomeBankAccount(bankAccountDetails);
                            setCurrentlyEditingConfig({
                                accountId: bankAccountDetails.bankAccountId,
                                originator: income.originator,
                                receiver: income.receiver,
                                isActive: true,
                                status: PaycheckRoutingStatus.ACTIVE,
                            });
                            router.navigate(
                                `/banking/paycheck-routing/income-routing?mode=${PaycheckRoutingRulesMode.CREATE}`,
                            );
                        }}
                    />
                )}
                {showSetupButton &&
                    (config?.status === PaycheckRoutingStatus.ACTIVE ||
                        config?.status === PaycheckRoutingStatus.INACTIVE) && (
                        <Button
                            variant={ButtonVariant.SECONDARY}
                            size={ButtonSize.SMALL}
                            text="Edit this paycheck"
                            disabled={
                                config.status === PaycheckRoutingStatus.INACTIVE
                            }
                            onPress={() => {
                                setSelectedIncome(income);
                                setSelectedIncomeBankAccount(
                                    bankAccountDetails,
                                );
                                setCurrentlyEditingConfig(config);
                                router.navigate(
                                    `/banking/paycheck-routing/income-routing?mode=${PaycheckRoutingRulesMode.EDIT}`,
                                );
                            }}
                        />
                    )}
            </YStack>
            {config && (
                <Sheet
                    isOpen={isConfirmDeactivatePaycheckConfigSheetOpen}
                    onClose={() => {
                        setIsConfirmDeactivatePaycheckConfigSheetOpen(false);
                        setIsLoadingActivationStatus(false);
                    }}
                    title="Are you sure?"
                    showCloseButton={true}
                >
                    <YStack marginTop={16} gap={24}>
                        <BodyMediumRegular color="$gray1000">
                            Confirming will turn off your recurring paycheck
                            split for {config.originator}.
                        </BodyMediumRegular>
                        <Button
                            variant={ButtonVariant.PRIMARY}
                            size={ButtonSize.LARGE}
                            text="Confirm"
                            onPress={async () => {
                                setIsConfirmDeactivatePaycheckConfigSheetOpen(
                                    false,
                                );
                                await updateConfig(false);
                            }}
                        />
                    </YStack>
                </Sheet>
            )}
        </YStack>
    );
};

export default DetectedIncomeCard;
